(() => {
  const WISHLIST_PATH_PATTERN = /\/apps\/wishlist(?:\/|$)/;
  const OBSERVED_SHADOW_ROOTS = new WeakSet();
  let wishlistSelectId = 0;
  const WISHLIST_SELECT_STYLES = `
    .wk-control:has(> select.wk-input),
    .wk-control:has(> select.wk-input) .wk-content,
    select.wk-input {
      background-color: #f7f7f7 !important;
    }
    .wk-control:has(> select.wk-input) .wk-content,
    .wk-control:has(> select.wk-input) .wk-text,
    .wk-control:has(> select.wk-input) .wk-icon,
    select.wk-input {
      color: #6b6b6b !important;
    }
    .wk-control:has(> select.wk-input) .wk-icon svg {
      stroke: currentColor !important;
    }
    .mds-wishlist-select-label {
      display: inline !important;
      color: #2e2f36 !important;
      font-weight: 600 !important;
      margin-inline-end: 0.25rem !important;
    }
    .mds-wishlist-select-context {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
    .wk-control:has(select.wk-input:focus-visible) {
      outline: 2px solid #39466b !important;
      outline-offset: 3px !important;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9) !important;
    }
    .wishlist-product-card .wk-cta-button:disabled,
    .wk-cta-button:disabled {
      opacity: 0.9 !important;
    }
  `;
  let scheduled = false;
  const pendingRoots = new Set();

  const focusableSvg = (svg) => {
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
  };

  const normalize = (value) => (value || "").replace(/\s+/g, " ").trim();
  const cssEscape = (value) => {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, "\\$&");
  };

  const queryAll = (root, selector) => {
    const matches =
      root instanceof Element && root.matches(selector) ? [root] : [];
    return matches.concat(Array.from(root.querySelectorAll(selector)));
  };

  const getText = (element) => normalize(element && element.textContent);

  const getProductName = (element) => {
    const container = element.closest(
      "li, article, .wk-product, .wk-item, .wk-card, .wk-grid__item, [data-product-id], [data-product-handle]",
    );
    const root =
      container || element.closest("wishlist-page") || element.parentElement;
    if (!root) return "";

    const titleSelectors = [
      "[data-product-title]",
      ".wk-product-title",
      ".wk-title",
      ".product-title",
      "h2",
      "h3",
      "h4",
      'a[href*="/products/"]',
    ];

    for (const selector of titleSelectors) {
      const candidates = Array.from(root.querySelectorAll(selector));
      const candidate = candidates.find(
        (item) => getText(item) && !item.querySelector("img"),
      );
      if (candidate) {
        return getText(candidate);
      }
    }

    const namedImage = root.querySelector('img[alt]:not([alt=""])');
    return namedImage ? normalize(namedImage.getAttribute("alt")) : "";
  };

  const patchWishlistHeading = (root) => {
    queryAll(root, "h2").forEach((heading) => {
      if (!/^wishlist$/i.test(getText(heading))) return;

      const h1 = document.createElement("h1");
      Array.from(heading.attributes).forEach((attribute) => {
        h1.setAttribute(attribute.name, attribute.value);
      });
      while (heading.firstChild) {
        h1.appendChild(heading.firstChild);
      }
      heading.replaceWith(h1);
    });
  };

  const patchWishlistRemoveButtons = (root) => {
    queryAll(
      root,
      'button[aria-label="Remove"], [role="button"][aria-label="Remove"]',
    ).forEach((button) => {
      const productName = getProductName(button);
      if (productName) {
        button.setAttribute(
          "aria-label",
          `Remove ${productName} from wishlist`,
        );
      }

      button.querySelectorAll("svg").forEach(focusableSvg);
    });
  };

  const patchWishlistProductLinks = (root) => {
    queryAll(root, 'a[href*="/products/"] img').forEach((image) => {
      const imageLink = image.closest("a");
      if (!imageLink) return;

      const href = imageLink.getAttribute("href");
      if (!href) return;

      const productName = getProductName(imageLink);
      if (!productName) return;

      const duplicateTitleLink = Array.from(
        root.querySelectorAll(`a[href="${cssEscape(href)}"]`),
      ).find((link) => link !== imageLink && getText(link));
      //  andrii-soviak issue->457-75
      if (duplicateTitleLink) {
        // 1. Disable image link completely (not just aria-hidden)
        imageLink.setAttribute("role", "presentation");
        imageLink.setAttribute("tabindex", "-1");

        // 2. Ensure title link has proper accessible name
        const titleText = normalize(getText(duplicateTitleLink)) || productName;
        duplicateTitleLink.setAttribute("aria-label", titleText);

        // 3. Image becomes decorative
        image.setAttribute("alt", productName);
      } else if (!normalize(image.getAttribute("alt"))) {
        image.setAttribute("alt", productName);
      }
    });
  };

  const patchWishlistSelects = (root) => {
    queryAll(root, "select.wk-input").forEach((select) => {
      const productName = getProductName(select);
      const ownerRoot = select.getRootNode();
      const control = select.closest(".wk-control");
      const visualContent = control && control.querySelector(".wk-content");
      const visualValue =
        visualContent && visualContent.querySelector(".wk-text");

      if (!select.id) {
        wishlistSelectId += 1;
        select.id = `mds-wishlist-size-select-${wishlistSelectId}`;
      }

      let labelId = select.getAttribute("data-mds-wishlist-label-id");
      let label =
        labelId && ownerRoot.querySelector(`#${cssEscape(labelId)}`);

      if (!label) {
        wishlistSelectId += 1;
        labelId = `mds-wishlist-size-label-${wishlistSelectId}`;
        label = document.createElement("label");
        label.id = labelId;
        label.className = "mds-wishlist-select-label";
        label.htmlFor = select.id;
        label.append(document.createTextNode("Size"));

        const separator = document.createElement("span");
        separator.setAttribute("aria-hidden", "true");
        separator.textContent = ":";
        label.append(separator);

        if (productName) {
          const context = document.createElement("span");
          context.className = "mds-wishlist-select-context";
          context.textContent = ` for ${productName}`;
          label.append(context);
        }

        if (visualContent) {
          visualContent.insertBefore(
            label,
            visualValue || visualContent.firstChild,
          );
        } else {
          const insertionTarget = control || select;
          insertionTarget.parentNode.insertBefore(label, insertionTarget);
        }

        select.setAttribute("data-mds-wishlist-label-id", labelId);
      }

      if (select.getAttribute("aria-labelledby") !== labelId) {
        select.setAttribute("aria-labelledby", labelId);
      }
    });
  };

  const patchWishlistContrast = (root) => {
    queryAll(root, "button, a").forEach((control) => {
      const label = `${getText(control)} ${control.getAttribute("aria-label") || ""}`;
      if (
        !/(add to cart|select options|view product|continue shopping|checkout)/i.test(
          label,
        )
      )
        return;

      control.setAttribute("data-mds-wishlist-contrast", "true");
    });
  };

  const addWishlistSelectStyles = (root) => {
    if (
      root === document ||
      root.querySelector("style[data-mds-wishlist-select-styles]")
    )
      return;

    const style = document.createElement("style");
    style.setAttribute("data-mds-wishlist-select-styles", "true");

    style.textContent = WISHLIST_SELECT_STYLES;
    root.appendChild(style);
  };

  const patchWishlist = (root) => {
    const hasWishlist =
      WISHLIST_PATH_PATTERN.test(window.location.pathname) ||
      queryAll(root, "wishlist-page, .wk-product, .wk-button, .wk-grid__item")
        .length;

    if (!hasWishlist) return;

    addWishlistSelectStyles(root);
    patchWishlistHeading(root);
    patchWishlistRemoveButtons(root);
    patchWishlistProductLinks(root);
    patchWishlistSelects(root);
    patchWishlistContrast(root);
  };

  const patchOlark = (root) => {
    queryAll(root, "svg title").forEach((title) => {
      if (!/olark/i.test(getText(title))) return;

      const svg = title.closest("svg");
      if (svg) {
        focusableSvg(svg);
      }
      title.remove();
    });

    queryAll(
      root,
      '#olark-container table, [id*="olark"] table, [class*="olark"] table, #olark-container [role="table"], [id*="olark"] [role="table"], [class*="olark"] [role="table"], #olark-container [role="grid"], [id*="olark"] [role="grid"], [class*="olark"] [role="grid"]',
    ).forEach((element) => {
      if (element.getAttribute("role") !== "presentation") {
        element.setAttribute("role", "presentation");
      }
    });

    queryAll(
      root,
      '#olark-container [role="row"], #olark-container [role="cell"], #olark-container [role="gridcell"], [id*="olark"] [role="row"], [id*="olark"] [role="cell"], [id*="olark"] [role="gridcell"], [class*="olark"] [role="row"], [class*="olark"] [role="cell"], [class*="olark"] [role="gridcell"]',
    ).forEach((element) => {
      if (element.getAttribute("role") !== "presentation") {
        element.setAttribute("role", "presentation");
      }
    });
  };

  const patchOptinMonster = (root) => {
    queryAll(root, '[role="dialog"], [role="alertdialog"]').forEach(
      (dialog) => {
        const id = dialog.getAttribute("id") || "";
        const className = dialog.getAttribute("class") || "";
        const isOptinMonster =
          /^om-/i.test(id) || /optin|campaign|om-/.test(className);
        if (!isOptinMonster) return;
        if (
          dialog.getAttribute("aria-label") ||
          dialog.getAttribute("aria-labelledby")
        )
          return;

        const heading = Array.from(
          dialog.querySelectorAll(
            "h1, h2, h3, [class*='title'], [class*='headline']",
          ),
        ).find((item) => getText(item));
        dialog.setAttribute(
          "aria-label",
          heading ? getText(heading) : "Promotional offer",
        );
      },
    );
  };

  const observeShadowRoots = (root, roots) => {
    const elements = root instanceof Element ? [root] : [];
    elements.push(...root.querySelectorAll("*"));

    elements.forEach((element) => {
      if (!element.shadowRoot || OBSERVED_SHADOW_ROOTS.has(element.shadowRoot))
        return;

      OBSERVED_SHADOW_ROOTS.add(element.shadowRoot);
      mutationObserver.observe(element.shadowRoot, {
        childList: true,
        subtree: true,
      });
      roots.push(element.shadowRoot);
    });
  };

  const patchThirdPartyAccessibility = () => {
    scheduled = false;
    const roots = Array.from(pendingRoots);
    const processedRoots = new Set();
    pendingRoots.clear();

    while (roots.length) {
      const root = roots.shift();
      if (
        !root ||
        processedRoots.has(root) ||
        typeof root.querySelectorAll !== "function"
      )
        continue;

      processedRoots.add(root);
      patchWishlist(root);
      patchOlark(root);
      patchOptinMonster(root);
      observeShadowRoots(root, roots);
    }
  };

  function schedulePatch(root = document) {
    if (root && typeof root.querySelectorAll === "function") {
      pendingRoots.add(root);
    }
    if (scheduled) return;
    scheduled = true;
    let completed = false;
    const runPatch = () => {
      if (completed) return;
      completed = true;
      window.clearTimeout(timeoutId);
      patchThirdPartyAccessibility();
    };
    const timeoutId = window.setTimeout(runPatch, 100);
    window.requestAnimationFrame(runPatch);
  }

  const scheduleFullPatchWhenIdle = () => {
    let completed = false;
    let idleId;
    const runPatch = () => {
      if (completed) return;
      completed = true;
      window.clearTimeout(timeoutId);
      if (idleId && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      schedulePatch(document);
    };
    const timeoutId = window.setTimeout(runPatch, 5000);

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(runPatch, { timeout: 5000 });
    }
  };

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (!mutation.addedNodes.length) return;

      mutation.addedNodes.forEach((node) => {
        if (
          node.nodeType === Node.ELEMENT_NODE ||
          node.nodeType === Node.DOCUMENT_FRAGMENT_NODE
        ) {
          schedulePatch(node);
        }
      });
    });
  });

  const style = document.createElement("style");
  style.textContent = `
    ${WISHLIST_SELECT_STYLES}

    [data-mds-wishlist-contrast="true"] {
      background-color: #39466b !important;
      border-color: #39466b !important;
      color: #fff !important;
    }
    [data-mds-wishlist-contrast="true"]:hover,
    [data-mds-wishlist-contrast="true"]:focus-visible {
      background-color: #2f3b5d !important;
      border-color: #2f3b5d !important;
      color: #fff !important;
    }
  `;
  document.head.appendChild(style);

  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  if (document.readyState === "complete") {
    scheduleFullPatchWhenIdle();
  } else {
    window.addEventListener("load", scheduleFullPatchWhenIdle, { once: true });
  }
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) scheduleFullPatchWhenIdle();
  });
  document.addEventListener("shopify:section:load", (event) => {
    schedulePatch(event.target);
  });
})();
